import { useState } from "react";
import { Group, Pill, Text, Button, Stack, Loader } from "@mantine/core";
import { IconLink } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

export interface LinkedItem {
    id: number;
    label: string;
}

interface ListProps {
    items: LinkedItem[];
    onUnlink: (id: number) => Promise<void>;
    onAddClick: () => void;
    emptyText?: string;
    canEdit?: boolean;
}

export function LinkedItemsList({ items, onUnlink, onAddClick, emptyText, canEdit = false }: ListProps) {
    const { t } = useTranslation();
    const [unlinking, setUnlinking] = useState<number | null>(null);

    const handleUnlink = async (id: number) => {
        setUnlinking(id);
        try {
            await onUnlink(id);
        } finally {
            setUnlinking(null);
        }
    };

    return (
        <Stack gap="xs">
            {items.length === 0 ? (
                <Text size="sm" c="dimmed">
                    {emptyText ?? t("common.linked.empty")}
                </Text>
            ) : (
                <Group gap="xs" wrap="wrap">
                    {items.map((item) => (
                        <Pill
                            key={item.id}
                            withRemoveButton={canEdit}
                            onRemove={canEdit ? () => handleUnlink(item.id) : undefined}
                            removeButtonProps={{
                                "aria-label": t("common.linked.unlinkAriaLabel", { label: item.label }),
                                disabled: unlinking !== null,
                            }}
                            styles={{ root: { maxWidth: 400 } }}
                        >
                            <Group gap={4} wrap="nowrap">
                                {unlinking === item.id && <Loader size={10} />}
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {item.label}
                                </span>
                            </Group>
                        </Pill>
                    ))}
                </Group>
            )}
            {canEdit && (
                <div>
                    <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconLink size={12} />}
                        onClick={onAddClick}
                        disabled={unlinking !== null}
                    >
                        {t("common.linked.addButton")}
                    </Button>
                </div>
            )}
        </Stack>
    );
}
